// Connection endpoint and readiness gate for the Primepoint MCP server
// (ai_engine app/mcp/server.py, deployed separately from the main API).

// IPv6 hostnames stay bracketed in both `location.hostname` and `URL.hostname`.
const LOCAL_HOSTNAMES = ["localhost", "127.0.0.1", "[::1]"];

// The production Capacitor webview serves the app from capacitor://localhost,
// so hostname alone cannot identify local development; without the protocol
// check the native app would advertise an unreachable localhost:5005 endpoint
// and count as MCP-ready. Non-HTTP(S) origins need REACT_APP_MCP_SERVER_URL.
const HTTP_PROTOCOLS = ["http:", "https:"];

function isLocalDevLocation(location: Pick<Location, "protocol" | "hostname">): boolean {
    return (
        HTTP_PROTOCOLS.includes(location.protocol) && LOCAL_HOSTNAMES.includes(location.hostname)
    );
}

export function isLocalMcpServer(serverUrl: string): boolean {
    try {
        return LOCAL_HOSTNAMES.includes(new URL(serverUrl).hostname);
    } catch {
        return false;
    }
}

export function defaultMcpServerUrl(
    location: Pick<Location, "protocol" | "hostname" | "origin">,
): string {
    return isLocalDevLocation(location)
        ? `${location.protocol}//${location.hostname}:5005/mcp`
        : `${location.origin}/mcp`;
}

export function mcpServerUrl(): string {
    if (process.env.REACT_APP_MCP_SERVER_URL) {
        return process.env.REACT_APP_MCP_SERVER_URL;
    }
    return defaultMcpServerUrl(window.location);
}

// The MCP server is deployed separately from the main API, and the deployed
// same-origin default above assumes an /mcp ingress that may not exist yet.
// Treat a deployment as MCP-ready only when an operator has explicitly pointed
// the build at a live server (REACT_APP_MCP_SERVER_URL) or on a local dev host
// (developers run the server themselves). Admin controls that enable the
// connection flow stay locked until then.
export function mcpServerReady(
    location: Pick<Location, "protocol" | "hostname"> = window.location,
): boolean {
    return Boolean(process.env.REACT_APP_MCP_SERVER_URL) || isLocalDevLocation(location);
}

// Stytch derives OAuth issuer identifiers from the domain a call transits:
// the SDK's default api base yields the legacy "stytch.com/<project-id>" form
// while the AS metadata MCP clients discover advertises the project domain, so
// a consent flow over the default base produces an RFC 9207 `iss` mismatch
// that spec-enforcing clients (e.g. Claude Code) reject after consent. Pinning
// is decided once at page load: Stytch always opens the consent page as a
// fresh full-page navigation, and the SDK forbids a second nested provider,
// so the app-wide client itself must be constructed pinned on that route.
export function consentClientOptions(
    location: Pick<Location, "pathname">,
    projectDomain: string | undefined = process.env.REACT_APP_STYTCH_PROJECT_DOMAIN,
): { customBaseUrl: string } | undefined {
    if (!projectDomain || !location.pathname.startsWith("/oauth/authorize")) {
        return undefined;
    }
    return { customBaseUrl: projectDomain };
}
